import React, { useEffect, useState } from 'react';
import { ApplicationState } from '../../../../../../../store/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import Placeholder from '../../../../../../../components/placeholder/Placeholder';
import BlazeTable from '../../../../../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from '../../../../../../../components/blazeTable/BlazeTable.types';
import { MessagingInspectionViewRequest } from '../../../../../../../types/rest/analyticsTypes';
import i18n from '../../../../../../../i18n';
import ElevioTooltip from '../../../../../../../components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import BzmIcon from '../../../../../../../components/icons/BzmIcon';
import { getSelectedCountTitle } from '../../../../../../../utils/miscUtil';
import { Button, Form } from 'react-bootstrap';
import {
  saveRecordingThunk,
  updateRecordingName,
} from '../../../../../../../store/mockServices/mockServiceDetail/mockServiceDetailActions';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useIsMount } from '../../../../../../../hooks/useIsMountHook';
import {
  deleteRecordingsByIds,
  getExistingRecordingMessages,
  pollForNewRecordingMessages,
} from '../../../../../../../store/mockServices/mockServiceActions';
import { MockService } from '../../../../../../../types/rest/mockServiceTypes';
import { confirm } from '../../../../../../../components/ui/modal/BlazeConfirmationModal';
import MessagingDetail from './Detail/MessagingDetail';
import { delay } from 'q';
import FullPageLoader from '../../../../../../../components/loaders/FullPageLoader';
import { formatDateTimeFromMillis } from '../../../../../../../utils/dateUtil';
import { showToastNotification } from '../../../../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../../../../store/ui/toasts/toastsTypes';
import SimpleLoader from '../../../../../../../components/loaders/SimpleLoader';

interface Props {
  disabled: boolean;
  mockService: MockService;
}

const RecordedMessages = ({ disabled, mockService }: Props) => {
  const isRecording = mockService.recorderStatus === 'RUNNING';
  const recordedMessages: MessagingInspectionViewRequest[] = useSelector(
    (state: ApplicationState) => state.mockServiceDetails.recordings.messages,
  );
  const totalMessages: number = useSelector(
    (state: ApplicationState) => state.mockServiceDetails.recordings.totalMessages,
  );

  const isPolling: boolean = useSelector((state: ApplicationState) => state.mockServiceDetails.recordings.isPolling);
  const isSaving: boolean = useSelector((state: ApplicationState) => state.mockServiceDetails.recordings.isSaving);
  const recordingName: string = useSelector((state: ApplicationState) => state.mockServiceDetails.recordings.name);
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const isMount = useIsMount();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isMount) {
      initComponentOnMount();
    }
    return () => {
      //Cleanup
      console.log('Unmounting RecordedMessages component...');
    };
  }, []);

  const initComponentOnMount = async () => {
    //Get the existing messages if there are none yet
    if (recordedMessages.length === 0) {
      setIsLoading(true);
      await dispatch(getExistingRecordingMessages(mockService));
      setIsLoading(false);
    }

    //Resume polling with delay if not running
    await delay(4000);
    if (isRecording && !isPolling) {
      dispatch(pollForNewRecordingMessages(mockService));
    }
  };

  const selectRecordedMessages = (items: any[]) => {
    const ids = _.map(items, item => item.id);
    setSelected(ids);
  };

  const deleteMessages = (id?: string) => {
    //Show Confirmation Dialog
    confirm({
      confirmation: <span>Are you sure about deleting the selected message(s)?</span>,
      yesOptionTitle: 'Delete',
      title: 'Delete Message from Recording',
    }).then(async () => {
      const toBeDeleted: any[] = id ? [id] : selected;
      dispatch(deleteRecordingsByIds(mockService, toBeDeleted, 'MESSAGING'));
    });
  };

  const buildColsDefinition = (): BlazeTableColDef[] => {
    return [
      {
        header: 'Timestamp',
        accessor: 'message',
        isSortable: true,
        cellComponent: (rowData: MessagingInspectionViewRequest) => {
          return <div>{formatDateTimeFromMillis(Number(rowData.timestamp))}</div>;
        },
        width: 120,
      },
      {
        header: 'Correlation ID',
        accessor: 'correlationId',
        isSortable: true,

        width: 220,
      },
      {
        header: 'Type',
        accessor: 'messageType',
        isSortable: true,
        width: 120,
      },

      {
        header: 'Virtual Inbound',
        accessor: '-',
        cellComponent: (message: MessagingInspectionViewRequest) => {
          return (
            <div>
              {message.originType && <small className={'mr-2'}>{message.originType}:</small>} {message.origin}
            </div>
          );
        },
        isSortable: true,
        width: 180,
      },
      {
        header: 'Live Outbound',
        accessor: '-',
        cellComponent: (rowData: MessagingInspectionViewRequest) => {
          if (_.isNil(rowData.destinations)) {
            return <span>-</span>;
          }
          return (
            <div>
              {rowData.destinations[0]?.destinationType && (
                <small className={'mr-2'}>{rowData.destinations[0]?.destinationType}:</small>
              )}
              {rowData.destinations[0]?.destinationName}
            </div>
          );
        },
        isSortable: true,
        width: 180,
      },
      {
        header: 'Actions',
        accessor: '-',
        cellComponent: (msg: MessagingInspectionViewRequest) => {
          return (
            <div className={'flex-table-actions'}>
              <button className={'btn btn-link-dark btn-no-hover'} onClick={() => deleteMessages(msg.id)}>
                <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
              </button>
            </div>
          );
        },
        isSortable: true,
        width: 80,
      },
    ];
  };

  const getTableOptions = (): BlazeTableOptions => {
    return {
      expandDetailComponent: (message: MessagingInspectionViewRequest) => <MessagingDetail messageRequest={message} />,
      selectAllDropdown: true,
      showRowExpander: true,
      columnDef: buildColsDefinition(),
      pageable: true,
      selectable: true,
      pageSize: 10,
      rowPadding: 8,
      loadingText: 'Getting Recorded Messages...',
      tableName: 'RecordedMessagesTable',
      noResultsText: 'No Messages Recorded Yet',
      minWidth: 900,
      sortable: true,
      selectAllType: null,
    };
  };

  const onUpdateRecordingName = event => {
    dispatch(updateRecordingName(event.target.value));
  };

  const onSaveRecording = () => {
    if (_.size(selected) === 0) {
      dispatch(showToastNotification(ToastType.Warning, 'Please select at least one message to save.'));
      setIsValidated(true);
      return;
    }
    if (_.isEmpty(recordingName)) {
      dispatch(showToastNotification(ToastType.Warning, 'Please enter a name for the recording.'));
      setIsValidated(true);
      return;
    }

    const dto = { messageIds: selected, serviceId: mockService.serviceId, recordingName: recordingName };
    dispatch(saveRecordingThunk(dto));
  };

  const getHeader = () => {
    return (
      <div className="mb-3">
        <Row>
          <Col lg={4} md={6}>
            <Form.Group controlId="qa-input-proxy-url">
              <Form.Label>Recording Name</Form.Label>
              <Form.Control
                className={isValidated && recordingName === '' && 'is-invalid'}
                disabled={disabled || isSaving}
                value={recordingName}
                type="text"
                autoComplete={'off'}
                name="recordingName"
                onChange={onUpdateRecordingName}
                placeholder={'Enter Recording Name...'}
              />
              {isValidated && recordingName === '' && (
                <div className="invalid-feedback">Please enter a name of the recording</div>
              )}
            </Form.Group>
          </Col>
          <Col lg={8} sm={6}>
            <Button
              variant={'primary'}
              disabled={isSaving}
              className={'position-absolute'}
              style={{ right: 16, top: -46 }}
              onClick={onSaveRecording}
            >
              {isSaving && <SimpleLoader className={'mr-3 d-inline-block'} showLoadingText={false} />}
              {!isSaving && <BzmIcon variant={'icon-bzm-save'} className={'mr-3'} />}
              Save Recording
            </Button>
          </Col>
        </Row>

        <h2 className={'text-uppercase mb-1'}>
          <span className={'mr-2'}>Recorded Messages</span>
          {getSelectedCountTitle(totalMessages, _.size(selected))}
          <ElevioTooltip elevioId={'Transactions_heading_help'} id={'TransactionsHelpId'} />
          <button
            id={`blk-delete`}
            className="btn btn-link btn-no-hover p-0 mx-2"
            title={'Remove Selected Messages'}
            onClick={() => deleteMessages()}
            disabled={disabled || _.isEmpty(selected)}
          >
            <BzmIcon variant={'icon-bzm-delete'} className={'icon-sm'} />
            <span className="sr-only">{i18n.t('transactions:table.removeTxns')}</span>
          </button>
        </h2>
        <p>List of recorded messages</p>
      </div>
    );
  };

  const getTable = () => {
    return (
      <BlazeTable
        selectedItems={selected}
        onSelectionChange={selectRecordedMessages}
        data={recordedMessages}
        options={getTableOptions()}
        isLoading={isLoading}
      />
    );
  };

  return (
    <div>
      {isLoading && <FullPageLoader loadingText="Loading Recorded Messages..." className={'text-center'} />}

      {!isLoading && (
        <>
          {_.size(recordedMessages) === 0 ? (
            <div className={'mt-8 mb-8'}>
              <Placeholder iconSize={85} titleSize={20} title={'No Recorded Messages Yet'} icon={'info'} />
            </div>
          ) : (
            <div>
              {getHeader()}
              {getTable()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RecordedMessages;
